import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { StageId } from '@joker/domain'
import { useTRPC } from '../trpc'
import { Panel, StatusChip, Loading, ErrorNote, useTooltip, ViewTitle } from '../ui'

/**
 * The one screen that answers "how is the business doing" without a follow-up
 * question.
 *
 * Built around the journey rather than around the tables: somebody raises a
 * hand, pays, gets an account, trains, finishes. Each step is a subset of the
 * one before it, so the percentages between them are real drop-offs rather
 * than ratios of unrelated populations — the old page divided this week's
 * trainers by everyone who had ever paid and printed "182% of paid", which is
 * not a number anybody can act on.
 */
export function Pipeline({
  onOpenBucket,
  onOpenStage,
}: {
  onOpenBucket: (id: string) => void
  onOpenStage: (id: StageId) => void
}) {
  const trpc = useTRPC()
  const pipeline = useQuery(trpc.analytics.pipeline.queryOptions())
  const buckets = useQuery(trpc.buckets.list.queryOptions())
  const { node: tipNode, bind } = useTooltip()
  /* Two channels, two sheets. A practice enquiry and a patient signup share no
     funnel, no conversion rate and no unit of revenue, so showing them on one
     page meant every figure had to say which it meant. */
  const [sheet, setSheet] = useState<'patients' | 'optometrists'>('patients')

  if (pipeline.error) return <ErrorNote error={pipeline.error} />
  if (!pipeline.data) return <Loading what="the pipeline" />

  const p = pipeline.data
  const maxStage = Math.max(1, ...p.stages.map((s) => s.count))
  const topOfFunnel = Math.max(1, ...p.funnel.map((f) => f.count))

  /* Ordered by how much it costs to leave it alone: somebody who paid and has
     no account has been charged for nothing, which outranks a quiet waitlist.
     Only buckets with members appear — an empty row is not a task. */
  const RANK = [
    'Paid 7+ days, no account yet',
    'Account created, never trained',
    'Onboarding stall',
    'High-investment win-back',
    'Optometrist-referred, stuck',
    'Tablet out',
    'Warm long-term waitlist',
  ]
  const attention = (buckets.data ?? [])
    .filter((b) => b.memberCount > 0 && RANK.includes(b.name))
    .sort((a, b) => RANK.indexOf(a.name) - RANK.indexOf(b.name))

  const money = p.money
  const momentum = money.lastMonthUsdCents
    ? Math.round(((money.thisMonthUsdCents - money.lastMonthUsdCents) / money.lastMonthUsdCents) * 100)
    : null

  const o = p.optometrist
  const sheetNav = (
    <div className="seg seg-inline" role="tablist" aria-label="Which channel">
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'patients'}
        onClick={() => setSheet('patients')}
      >
        Patients
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'optometrists'}
        onClick={() => setSheet('optometrists')}
      >
        Optometrists
      </button>
    </div>
  )

  if (sheet === 'optometrists') {
    const perPractice = o.practices === 0 ? 0 : Math.round(o.revenueUsdCents / o.practices)
    return (
      <>
        {sheetNav}
        <ViewTitle suit="spade">The optometrist channel</ViewTitle>
        <p className="view-sub">
          Practices we work with and the patients who come through them. A practice bills its own
          patient and settles with us, so their revenue is the agreed cut rather than an order —
          which is why none of this belongs in the patient funnel next door.
        </p>

        <div className="tiles">
          <Tile
            k="Enquiries"
            v={o.enquiries.toLocaleString()}
            d="practices who have asked, not yet signed"
          />
          <Tile k="Practices" v={o.practices.toLocaleString()} d="confirmed and listed" />
          <Tile
            k="Patients enrolled"
            v={o.patients.toLocaleString()}
            d={`${pct(o.patients, p.totalPeople)}% of all patients`}
          />
          <Tile
            k="Paying"
            v={o.paying.toLocaleString()}
            d={`${pct(o.paying, o.patients)}% of their patients`}
          />
          <Tile k="Revenue" v={usd(o.revenueUsdCents)} d="our cut, in USD net of VAT" />
          <Tile k="Per practice" v={usd(perPractice)} d="average across all of them" />
        </div>

        <Panel title="Practice by practice" note="ordered by revenue">
          <table className="data">
            <thead>
              <tr>
                <th>Practice</th>
                <th className="num">Patients</th>
                <th className="num">Training</th>
                <th className="num">Paying</th>
                <th className="num">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {o.byPractice.map((x) => (
                <tr key={`${x.name}-${x.locationName ?? ''}`}>
                  <td>
                    <strong>{x.name}</strong>
                    {x.locationName && <span className="muted sub-line"> · {x.locationName}</span>}
                  </td>
                  <td className="num">{x.patients}</td>
                  <td className="num">{x.training}</td>
                  <td className="num">{x.paying || <span className="muted">—</span>}</td>
                  <td className="num">
                    {x.revenueUsdCents > 0 ? <strong>{usd(x.revenueUsdCents)}</strong> : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="footnote">
            A practice with patients and no revenue has nobody classified as paying yet — set that
            on the practice&rsquo;s own page and their cut starts counting. Francs are converted on
            the day each patient was counted as paying, which is the only basis on which a Swiss
            practice and a Georgian one can be added together.
          </p>
        </Panel>
        {tipNode}
      </>
    )
  }

  return (
    <>
      {sheetNav}
      <ViewTitle suit="spade">Where everybody stands</ViewTitle>
      <p className="view-sub">
        Everyone Oryx has touched, from the first time they raised a hand to the day they finish
        the program. A lead is somebody with no Oryx account — they may still have paid, and when
        they bought for somebody else the money follows the patient who trains on it. Optometrist
        enquiries are a different channel and live on the sheet next door.
      </p>

      {/* The six figures worth knowing before anything else. */}
      <div className="tiles">
        <Tile
          k="Leads"
          v={p.leadsWaiting.toLocaleString()}
          d="have not paid yet"
        />
        <Tile
          k="Patients"
          v={p.totalPeople.toLocaleString()}
          d={`${p.everTrained} of them have trained`}
        />
        {/* "Paying patients", not "Paying": the funnel counts one more, because
            a lead can pay before they have an account. Two figures a page apart
            differing by one reads as a fault unless both say who they count. */}
        <Tile
          k="Paying patients"
          v={p.paidPeople.toLocaleString()}
          d={`${pct(p.paidPeople, p.totalPeople)}% of all patients`}
        />
        <Tile
          k="Revenue"
          v={usd(money.revenueUsdCents)}
          d="in USD, net of VAT, at each payment's own rate"
        />
        <Tile
          k="Training, last 7 days"
          v={p.trainingThisWeek.toLocaleString()}
          d={`rolling window to today · ${pct(p.trainingThisWeek, p.everTrained)}% of the ${p.everTrained} who have ever trained`}
        />
        {/* Against the only population that could have finished: trained at
            least once and has a programme to finish. */}
        <Tile
          k="Finished"
          v={`${p.completed.toLocaleString()} / ${p.trainedWithProgram.toLocaleString()}`}
          d={`${pct(p.completed, p.trainedWithProgram)}% of those training towards a program`}
        />
      </div>

      <Panel title="Lifecycle stages" note="select a stage to see who is in it">
        <div className="funnel">
          {p.stages.map((s) => {
            /* A lead is not a patient — no Oryx account, and not on the
               Patients screen — so this row is counted and worded in leads,
               and opens the Leads list rather than the patient table. */
            const isLead = s.stage === 'lead'
            return (
            <button
              key={s.stage}
              type="button"
              className="frow"
              onClick={() => onOpenStage(s.stage as StageId)}
              aria-label={`${s.label}: ${s.count} ${isLead ? 'leads' : 'patients'}. Open the list.`}
              {...bind(
                <>
                  <b>{s.label}</b>
                  <div className="r">
                    <span>{s.definition}</span>
                  </div>
                  <div className="r">
                    <span>{isLead ? 'Leads' : 'Patients'}</span>
                    <span>{s.count}</span>
                  </div>
                  {!isLead && (
                    <div className="r">
                      <span>Share of patients</span>
                      <span>{pct(s.count, p.totalPeople)}%</span>
                    </div>
                  )}
                  <div className="hint">
                    {isLead ? 'Click to see the leads' : 'Click to see the patients'}
                  </div>
                </>,
              )}
            >
              <span className="lbl">
                {s.label} <StatusChip severity={s.severity} />
                {/* Most leads arrive from the website form; the waitlist is a
                    minority, and the old label implied the opposite. */}
                {s.stage === 'lead' && p.leadsFromWaitlist > 0 && (
                  <span className="muted sub-line"> · {p.leadsFromWaitlist} from the waitlist</span>
                )}
              </span>
              <span className="bar-track">
                <span className="bar" style={{ width: `${Math.max(1, (s.count / maxStage) * 100)}%` }} />
              </span>
              <span className="cnt num">{s.count}</span>
              <span className={`wow num ${s.deltaWeek >= 0 ? 'up' : 'down'}`}>
                {s.deltaWeek > 0 ? '+' : ''}
                {s.deltaWeek} wk
              </span>
              <span className="chev" aria-hidden="true">›</span>
            </button>
            )
          })}
        </div>
        <p className="footnote">
          Stages are mutually exclusive and recomputed on every sync. Every transition is written
          to a log, which is what makes &ldquo;how long does paid &rarr; first session actually
          take?&rdquo; answerable.
        </p>
      </Panel>

      <Panel title="The journey" note="paying customers, each step a subset of the one above">
        <div className="journey">
          {p.funnel.map((f, i) => (
            <div key={f.id} className="jstep">
              <div className="jhead">
                <span className="jlabel">{f.label}</span>
                {f.ofPrevious != null && (
                  <span
                    className={`jpct ${f.ofPrevious >= 60 ? 'up' : f.ofPrevious >= 30 ? '' : 'down'}`}
                  >
                    {f.ofPrevious}% carried through
                  </span>
                )}
              </div>
              <div
                className="jbar"
                {...bind(
                  <>
                    <b>{f.label}</b>
                    <div className="r">
                      <span>{f.hint}</span>
                    </div>
                    <div className="r">
                      <span>People</span>
                      <span>{f.count.toLocaleString()}</span>
                    </div>
                    {i > 0 && p.funnel[i - 1] && (
                      <div className="r">
                        <span>Lost at this step</span>
                        <span>{(p.funnel[i - 1]!.count - f.count).toLocaleString()}</span>
                      </div>
                    )}
                  </>,
                )}
              >
                <span
                  className="jfill"
                  style={{ width: `${Math.max(2, (f.count / topOfFunnel) * 100)}%` }}
                />
                <span className="jn num">{f.count.toLocaleString()}</span>
              </div>
              <span className="jhint">{f.hint}</span>
            </div>
          ))}
        </div>
        <p className="footnote">
          Median <strong>{p.medianDaysLeadToPaid == null ? '—' : `${p.medianDaysLeadToPaid} days`}</strong>{' '}
          from joining the waitlist to paying, then{' '}
          <strong>
            {p.medianDaysPaidToFirstSession == null
              ? '—'
              : `${Math.round(p.medianDaysPaidToFirstSession)} days`}
          </strong>{' '}
          from paying to the first session. Both come from the transition log rather than from
          current state, which cannot answer either question.
        </p>
      </Panel>

      {/* The worklist beside the money, now that the stages have moved up. */}
      <div className="grid-2">
        <Panel title="Needs a human this week" note="worst first">
          <div className="att">
            {attention.length === 0 && <p className="footnote">Nothing waiting. Enjoy it.</p>}
            {attention.map((b) => (
              <button key={b.id} type="button" className="att-item" onClick={() => onOpenBucket(b.id)}>
                <span className="att-n num">{b.memberCount}</span>
                <span className="att-t">
                  <b>{b.name}</b>
                  {b.description}
                </span>
                <span className="chev" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </Panel>
        <div className="stack">

          <Panel title="Where the money comes from" note="all figures in USD">
            <table className="stat-table">
              <tbody>
                {money.bySource.map((m) => (
                  <tr key={m.source}>
                    <th scope="row">{m.source === 'optometrist' ? 'Through a practice' : 'Direct'}</th>
                    <td className="num">{m.people} patients</td>
                    <td className="num">
                      <strong>{usd(m.usdCents)}</strong>
                    </td>
                    <td className="num muted">{pct(m.usdCents, money.revenueUsdCents)}%</td>
                  </tr>
                ))}
                <tr>
                  <th scope="row">This month</th>
                  <td className="num muted">
                    {momentum == null ? '' : `${momentum > 0 ? '+' : ''}${momentum}% on last`}
                  </td>
                  <td className="num">
                    <strong>{usd(money.thisMonthUsdCents)}</strong>
                  </td>
                  <td className="num muted">
                    {money.lastMonthUsdCents > 0 ? `${usd(money.lastMonthUsdCents)} last` : ''}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="footnote">
              A practice bills its own patient and settles with us, so their revenue is the agreed
              cut rather than an order. Francs and shekels are converted on the day each payment was
              taken and VAT is removed, which is the only basis on which the two can be added.
            </p>
          </Panel>

          <Panel title="Also worth knowing">
            <dl className="mini">
              <dt>VIPs</dt>
              <dd className="num">{p.vips}</dd>
              <dt>Tablets out</dt>
              <dd className="num">{p.tabletsOut}</dd>
              <dt>Leads who paid</dt>
              <dd className="num">{p.leadsPaid}</dd>
            </dl>
          </Panel>
        </div>
      </div>
      {tipNode}
    </>
  )
}

function Tile({ k, v, d }: { k: string; v: string; d: string }) {
  return (
    <div className="tile">
      <div className="k">{k}</div>
      <div className="v num">{v}</div>
      <div className="d">{d}</div>
    </div>
  )
}

const pct = (a: number, b: number) => (b === 0 ? 0 : Math.round((a / b) * 100))
const usd = (cents: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(cents / 100)
